import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import ConversationList from '../components/sniggle/ConversationList';
import ChatWindow from '../components/sniggle/ChatWindow';
import ActivityFeed from '../components/sniggle/ActivityFeed';
import { Users, MessageCircle, Plus, X } from 'lucide-react';

const Sniggle = () => {
    const { fetchWithAuth, user } = useContext(AuthContext);
    const { showToast } = useToast();
    const { userId: urlUserId } = useParams();
    const navigate = useNavigate();

    const [activities, setActivities] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingActivities, setLoadingActivities] = useState(true);
    const [loadingConvos, setLoadingConvos] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [feedOpen, setFeedOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const pollIntervalRef = useRef(null);
    const messagesRef = useRef(messages);
    const activeConversationRef = useRef(null);

    useEffect(() => { messagesRef.current = messages; }, [messages]);
    useEffect(() => { activeConversationRef.current = activeConversation; }, [activeConversation]);

    useEffect(() => {
        loadConversations();
        loadActivities();
        pollIntervalRef.current = setInterval(pollUpdates, 5000);
        return () => clearInterval(pollIntervalRef.current);
    }, []);

    useEffect(() => {
        if (urlUserId && !loadingConvos) {
            const existing = conversations.find(c => c.partner.id === parseInt(urlUserId));
            if (existing) selectConversation(existing);
        }
    }, [urlUserId, loadingConvos]);

    const isUserOnline = (lastSeen) => {
        if (!lastSeen) return false;
        return (new Date() - new Date(lastSeen)) / (1000 * 60) < 5;
    };

    const loadActivities = async () => {
        try {
            const res = await fetchWithAuth('/api/social/feed/');
            if (res.ok) setActivities(await res.json());
        } catch (err) { console.error(err); }
        finally { setLoadingActivities(false); }
    };

    const loadConversations = async () => {
        try {
            const res = await fetchWithAuth('/api/sniggle/conversations/');
            if (res.ok) {
                const data = await res.json();
                setConversations(data.map(conv => ({
                    ...conv,
                    partner: { ...conv.partner, is_online: isUserOnline(conv.partner.last_seen) }
                })));
            }
        } catch (err) { console.error(err); }
        finally { setLoadingConvos(false); }
    };

    const pollUpdates = async () => {
        try {
            const res = await fetchWithAuth('/api/sniggle/conversations/');
            if (res.ok) {
                const data = await res.json();
                setConversations(data.map(conv => ({
                    ...conv,
                    partner: { ...conv.partner, is_online: isUserOnline(conv.partner.last_seen) }
                })));
            }
            if (activeConversationRef.current) {
                const msgRes = await fetchWithAuth(`/api/sniggle/conversations/${activeConversationRef.current.id}/messages/`);
                if (msgRes.ok) {
                    const msgData = await msgRes.json();
                    if (msgData.length !== messagesRef.current.length) setMessages(msgData);
                }
            }
        } catch (e) { /* silent */ }
    };

    const selectConversation = async (conv) => {
        setActiveConversation(conv);
        setLoadingMessages(true);
        // On mobile, close sidebar when opening a chat
        if (window.innerWidth < 768) setSidebarOpen(false);
        try {
            const res = await fetchWithAuth(`/api/sniggle/conversations/${conv.id}/messages/`);
            if (res.ok) {
                setMessages(await res.json());
                fetchWithAuth(`/api/sniggle/conversations/${conv.id}/mark_read/`, { method: 'POST' });
                setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
            }
        } catch (e) {
            showToast('Konversation konnte nicht geladen werden', 'error');
        } finally { setLoadingMessages(false); }
    };

    const handleSendMessage = async (content) => {
        if (!activeConversation) return;
        const tempId = 'temp-' + Date.now();
        const tempMsg = { id: tempId, sender: user.id, content, timestamp: new Date().toISOString(), is_read: false, message_type: 'TEXT' };
        setMessages(prev => [...prev, tempMsg]);
        try {
            const res = await fetchWithAuth('/api/sniggle/messages/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ recipient_id: activeConversation.partner.id, content })
            });
            if (res.ok) {
                const realMsg = await res.json();
                setMessages(prev => prev.map(m => m.id === tempId ? realMsg : m));
                setConversations(prev => {
                    const others = prev.filter(c => c.id !== activeConversation.id);
                    return [{ ...activeConversation, last_message: realMsg, updated_at: realMsg.timestamp }, ...others];
                });
            } else throw new Error('Send failed');
        } catch (e) {
            showToast('Nachricht konnte nicht gesendet werden', 'error');
            setMessages(prev => prev.filter(m => m.id !== tempId));
        }
    };

    const totalUnread = conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0);

    return (
        <div className="h-[calc(100vh-64px)] md:h-[calc(100vh-80px)] w-full flex overflow-hidden bg-gray-50 dark:bg-[#0d0d0d]">

            {/* ── Sidebar ── */}
            <aside className={`
                flex-shrink-0 flex flex-col
                w-full md:w-80 lg:w-[340px]
                bg-white dark:bg-[#141414]
                border-r border-gray-100 dark:border-white/5
                transition-all duration-300 ease-out
                ${activeConversation && !sidebarOpen ? '-translate-x-full md:translate-x-0 absolute md:relative z-20 h-full' : 'translate-x-0'}
            `}>
                {/* Sidebar Header */}
                <div className="px-5 pt-6 pb-4 flex-shrink-0">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <h1 className="text-xl font-black text-gray-900 dark:text-white tracking-tight">Nachrichten</h1>
                            {totalUnread > 0 && (
                                <p className="text-xs text-plantlix-500 font-bold mt-0.5">{totalUnread} ungelesen</p>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => navigate('/friends')}
                                className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-white/5 hover:bg-plantlix-50 dark:hover:bg-plantlix-900/20 text-gray-500 dark:text-gray-400 hover:text-plantlix-600 dark:hover:text-plantlix-400 flex items-center justify-center transition-all"
                                title="Freunde"
                            >
                                <Users className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => navigate('/friends?tab=add')}
                                className="w-9 h-9 rounded-xl bg-plantlix-500 hover:bg-plantlix-600 text-white flex items-center justify-center transition-all shadow-lg shadow-plantlix-500/30"
                                title="Neuer Chat"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Conversation List */}
                <div className="flex-1 overflow-y-auto overscroll-contain">
                    <ConversationList
                        conversations={conversations}
                        activeConversationId={activeConversation?.id}
                        onSelect={selectConversation}
                        loading={loadingConvos}
                    />
                </div>

                {/* Community Feed Toggle */}
                <div className="flex-shrink-0 border-t border-gray-100 dark:border-white/5">
                    <button
                        onClick={() => setFeedOpen(p => !p)}
                        className="w-full flex items-center justify-between px-5 py-3 text-sm font-bold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-all"
                    >
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-plantlix-500 animate-pulse" />
                            Community Feed
                        </div>
                        <svg
                            className={`w-4 h-4 transition-transform ${feedOpen ? 'rotate-180' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                    </button>

                    {feedOpen && (
                        <div className="max-h-80 overflow-y-auto border-t border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-black/20">
                            <ActivityFeed activities={activities} loading={loadingActivities} compact />
                        </div>
                    )}
                </div>
            </aside>

            {/* ── Chat Area ── */}
            <main className={`
                flex-1 flex flex-col min-w-0
                bg-gray-50 dark:bg-[#0d0d0d]
                transition-all duration-300
                ${activeConversation || !sidebarOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
                ${!activeConversation && sidebarOpen ? 'hidden md:flex' : 'flex'}
            `}>
                {activeConversation ? (
                    <ChatWindow
                        activeConversation={activeConversation}
                        messages={messages}
                        currentUser={user}
                        onSendMessage={handleSendMessage}
                        loadingMessages={loadingMessages}
                        onBack={() => { setSidebarOpen(true); setActiveConversation(null); }}
                        onAcceptDuel={(id) => console.log('accept', id)}
                        onDeclineDuel={(id) => console.log('decline', id)}
                        onInviteDuel={async () => {
                            try {
                                const resp = await fetchWithAuth('/api/duel/invite-via-sniggle/', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ recipient_id: activeConversation.partner.id })
                                });
                                if (resp.ok) {
                                    showToast(`Duel-Einladung an ${activeConversation.partner.username} gesendet! 🎮`, 'success');
                                    selectConversation(activeConversation);
                                } else {
                                    const error = await resp.json();
                                    showToast(error.error || 'Fehler beim Senden der Duel-Einladung', 'error');
                                }
                            } catch (err) {
                                showToast('Fehler beim Senden der Duel-Einladung', 'error');
                            }
                        }}
                    />
                ) : (
                    <div className="hidden md:flex flex-1 items-center justify-center">
                        <div className="text-center max-w-sm px-8 animate-in fade-in duration-500">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-[2rem] bg-gradient-to-br from-plantlix-500/20 to-plantlix-700/10 border border-plantlix-500/20 flex items-center justify-center">
                                <MessageCircle className="w-10 h-10 text-plantlix-500" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-2 tracking-tight">
                                Deine Chats
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400 leading-relaxed text-sm">
                                Wähle eine Konversation aus der Liste oder starte eine neue Unterhaltung mit deinen Freunden.
                            </p>
                            <button
                                onClick={() => navigate('/friends?tab=add')}
                                className="mt-6 px-6 py-3 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-2xl font-bold text-sm shadow-lg shadow-plantlix-500/25 hover:shadow-plantlix-500/40 transition-all hover:scale-105 active:scale-95"
                            >
                                + Neuen Chat starten
                            </button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default Sniggle;
